import { Button } from '@invent/wl-ui-kit-next/ui';
import { RotateCcw, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted, ReasonDialog } from '@/design';
import type { IEtagged } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  IOversightResponseDetail,
  IResponseRecycled,
  ResponseStatus
} from '../api/oversight-contracts';
import { NOT_REREAD } from '../pages/person-hub/said';
import { responsePath } from '../paths';
import {
  RECYCLE_REASON_MAX,
  RECYCLE_SAYS,
  RECYCLE_WORDS,
  landedSentence,
  moveOf,
  recycleRefusal,
  restoreSays,
  sendMove,
  type IRecycleSubject,
  type RecycleMove
} from '../recycle-bin';

/**
 * One response's recycle bin press (SR-470's D3, after SR-421's account status press): "Move to
 * recycle bin…" on a draft, submitted or locked response, "Restore…" on a recycled one, nothing on
 * an archived one. Drawn on the record page's survey strip, for the response the strip shows, and
 * in the response page's header — the record page first, since the client's staff work from a
 * person, a team or a firm and do not open the Responses list.
 *
 * **A confirmed press with an optional reason** (`ReasonDialog`, survey-api's 2 000 characters said
 * and never enforced), whose words say what the 200 does not: the advisor no longer sees the
 * response, nothing is deleted, and a restore returns it as the status it left. The move and its
 * response are fixed as the dialog opens: the read a landing makes flips the status while the
 * dialog is on its way out, and its title must not turn into the other move's; a re-read under
 * the open dialog may put another response on the strip, and the move must not follow it.
 *
 * **What comes back is said under the press** and kept until the next press: a landing names the
 * response (the strip may show another one of the survey once read again) and is followed by the
 * caller's `onMoved` — the strip re-reads the hub, the response page adopts the answer. A refusal
 * is said where `recycleRefusal` puts it: in the dialog with the reason kept, or under the press
 * with the page read again (`onReread`) where its read is out of date, and with "Open it" to the
 * live response that holds the slot a restore asked for.
 *
 * **Drawn off, never hidden**, with `NotPermitted` under it, without `responses.annotate` — the
 * routes' policy. Whether the move is allowed is survey-api's to judge; the press is offered by the
 * status the page read.
 */

/** The response a press acts on, as the strip's row or the response page's detail gives it. */
export interface IRecyclable {
  responseId: string;
  status: ResponseStatus;
  /** What the move quotes in `If-Match` — the detail's `ETag`, or `"<revision>"` from a row. */
  etag: string;
  recycled?: Nullable<IResponseRecycled>;
  subject: IRecycleSubject;
}

interface ISaid {
  tone: 'done' | 'refused';
  text: string;
  /** `live-slot-taken`'s holder, opened by "Open it". */
  holder: Nullable<string>;
}

export function RecyclePress({
  response,
  onMoved,
  onReread
}: {
  response: IRecyclable;
  /** After a landing: resolves `false` where the page could not take the answer in. */
  onMoved: (moved: IEtagged<IOversightResponseDetail>) => Promise<boolean>;
  /** After a refusal that says the page's read is out of date: `false` where the re-read failed. */
  onReread: () => Promise<boolean>;
}) {
  const permitted = useCan()('responses.annotate');
  const next = moveOf(response.status);
  const [move, setMove] = React.useState<RecycleMove>(next ?? 'recycle');
  // The response the press was made on: a re-read under the open dialog (the strip's poll while
  // a lookup is pending) may put another response of the survey on the strip, and the move must
  // still go to the one the dialog was opened for.
  const [held, setHeld] = React.useState(response);
  const [open, setOpen] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<ISaid>>(null);
  const offId = React.useId();

  const confirm = async (reason: string): Promise<Nullable<string>> => {
    const { responseId, etag, subject } = held;
    let moved: IEtagged<IOversightResponseDetail>;
    try {
      moved = await sendMove(move, responseId, etag, reason);
    } catch (error) {
      const refusal = recycleRefusal(error, move, subject);
      if (refusal.kind === 'dialog') return refusal.sentence;
      setSaid({ tone: 'refused', text: refusal.sentence, holder: refusal.holder });
      if (refusal.reread && !(await onReread())) {
        setSaid({ tone: 'refused', text: refusal.sentence + NOT_REREAD, holder: refusal.holder });
      }
      return null;
    }
    const landed = landedSentence(subject, moved.body.status);
    setSaid({ tone: 'done', text: landed, holder: null });
    if (!(await onMoved(moved))) setSaid({ tone: 'done', text: landed + NOT_REREAD, holder: null });
    return null;
  };

  // An archived response is neither moved nor restored; what an earlier press was told stays, and
  // a dialog a re-read made archived under it stays up until its answer.
  if (next === null && said === null && !open) return null;

  const Icon = next === 'restore' ? RotateCcw : Trash2;

  return (
    <div data-recycle-press className="grid justify-items-end gap-1">
      {next !== null && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={!permitted}
          aria-describedby={permitted ? undefined : offId}
          onClick={() => {
            setMove(next);
            setHeld(response);
            setSaid(null);
            setOpen(true);
          }}
        >
          <Icon className="size-4" aria-hidden />
          {RECYCLE_WORDS[next].press}
        </Button>
      )}
      {!permitted && next !== null && (
        <NotPermitted
          id={offId}
          what="moving a response to the recycle bin or restoring it"
          className="max-w-xs text-right"
        />
      )}
      {said && (
        <p
          role={said.tone === 'refused' ? 'alert' : 'status'}
          data-recycle-said={said.tone}
          className={
            said.tone === 'refused'
              ? 'max-w-md text-right text-sm text-destructive'
              : 'max-w-md text-right text-sm text-muted-foreground'
          }
        >
          {said.text}
          {said.holder && <HolderLink holder={said.holder} />}
        </p>
      )}

      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={RECYCLE_WORDS[move].title}
        description={move === 'restore' ? restoreSays(held.recycled?.from) : RECYCLE_SAYS}
        required={false}
        max={RECYCLE_REASON_MAX}
        confirmLabel={RECYCLE_WORDS[move].confirm}
        onConfirm={confirm}
      >
        <p className="text-xs text-muted-foreground">
          The reason is kept with the move in the response’s history.
        </p>
      </ReasonDialog>
    </div>
  );
}

/** "Open it" after a `live-slot-taken` sentence: the live response that holds the slot. */
export function HolderLink({ holder }: { holder: string }) {
  return (
    <>
      {' '}
      <Link
        to={responsePath(holder)}
        className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
      >
        Open it
      </Link>
    </>
  );
}
